import React, { forwardRef } from 'react';
import { ReactComponent as ChevronDownIcon } from '@/img/chevron-down.svg';
import UISpinner from '@/components/shared/UISpinner'; // Import UISpinner
import './UISelectWithLabel.scss';

export interface UISelectOption {
  value: string | number;
  label: string;
  disabled?: boolean;
}

export interface UISelectWithLabelProps
  extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  /**
   * Варіант стилю селекта
   */
  variant?: 'default' | 'error' | 'success';

  /**
   * Розмір селекта
   */
  size?: 'small' | 'medium' | 'large';

  /**
   * Додатковий CSS клас
   */
  className?: string;

  /**
   * Повний width батьківського контейнера
   */
  fullWidth?: boolean;

  /**
   * Текст помилки (показується під селектом)
   */
  error?: string;

  /**
   * Лейбл над селектом
   */
  label: string;

  /**
   * Ширина лейбла в відсотках
   */
  labelWidth?: number;

  /**
   * Опції для вибору
   */
  options?: UISelectOption[];

  /**
   * Placeholder текст (перша опція)
   */
  placeholder?: string;

  /**
   * Стан завантаження для відображення спінера
   */
  loading?: boolean; // Add loading prop
}

const baseClass = 'UISelectWithLabel';

const UISelectWithLabel = forwardRef<HTMLSelectElement, UISelectWithLabelProps>(
  (
    {
      variant = 'default',
      size = 'medium',
      className = '',
      fullWidth = false,
      error,
      label,
      labelWidth,
      options = [],
      placeholder = 'Select Option',
      children,
      loading = false, // Destructure loading prop
      ...props
    },
    ref,
  ) => {
    const selectClasses = [
      baseClass,
      `${baseClass}--${variant}`,
      `${baseClass}--${size}`,
      fullWidth && `${baseClass}--full-width`,
      error && `${baseClass}--error`,
      className,
    ]
      .filter(Boolean)
      .join(' ');

    const renderLabel = () => {
      const parts = label.split('*');
      return parts.map((part, index) => (
        <React.Fragment key={index}>
          {part}
          {index < parts.length - 1 && <span className="UISelectWithLabel__asterisk">*</span>}
        </React.Fragment>
      ));
    };

    const labelStyle = labelWidth ? { flexBasis: `${labelWidth}%` } : {};

    return (
      <div className={`${baseClass} ${className}`}>
        <label className={`${baseClass}__label`} style={labelStyle}>{renderLabel()}</label>
        <div className={`${baseClass}__wrapper`}>
          <select ref={ref} className={`${baseClass}__select`} {...props}>
            {placeholder && (
              <option value="" disabled>
                {placeholder}
              </option>
            )}

            {options.map((option, index) => (
              <option
                key={option.value || index}
                value={option.value}
                disabled={option.disabled}
              >
                {option.label}
              </option>
            ))}

            {children}
          </select>

          <div className={`${baseClass}__arrow`}>
            {loading ? ( // Conditionally render spinner
              <UISpinner size="small" />
            ) : (
              <ChevronDownIcon className={`${baseClass}__chevron-icon`} />
            )}
          </div>
        </div>

        {error && <span className={`${baseClass}__error-text`}>{error}</span>}
      </div>
    );
  },
);

UISelectWithLabel.displayName = 'UISelectWithLabel';

export default UISelectWithLabel;
